const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Animated GIFs cannot safely be resized via canvas: it would silently discard
// their animation. A small parser counts image frames without decoding them.
async function hasMultipleGifFrames(blob) {
  // FileReader also supports older Safari/JSDOM environments that lack Blob.arrayBuffer.
  const buffer = typeof blob.arrayBuffer === "function"
    ? await blob.arrayBuffer()
    : await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Không thể đọc GIF."));
      reader.readAsArrayBuffer(blob);
    });
  const bytes = new Uint8Array(buffer);
  const header = String.fromCharCode(...bytes.slice(0, 6));
  if (header !== "GIF87a" && header !== "GIF89a") {
    throw new Error("Tệp GIF không hợp lệ.");
  }
  if (bytes.length < 13) throw new Error("Tệp GIF không hợp lệ.");
  let offset = 13;
  const globalFlags = bytes[10];
  if (globalFlags & 0x80) offset += 3 * (1 << ((globalFlags & 0x07) + 1));

  const skipBlocks = () => {
    while (offset < bytes.length) {
      const size = bytes[offset++];
      if (size === 0) return;
      offset += size;
    }
    throw new Error("Tệp GIF bị lỗi hoặc thiếu dữ liệu.");
  };

  let frames = 0;
  while (offset < bytes.length) {
    const marker = bytes[offset++];
    if (marker === 0x3b) return frames > 1;
    if (marker === 0x21) {
      // Extensions have one fixed first block followed by variable data blocks.
      offset += 1;
      skipBlocks();
    } else if (marker === 0x2c) {
      if (offset + 9 > bytes.length) throw new Error("Tệp GIF không hợp lệ.");
      const flags = bytes[offset + 8];
      offset += 9;
      if (flags & 0x80) offset += 3 * (1 << ((flags & 0x07) + 1));
      offset += 1; // LZW minimum code size
      skipBlocks();
      frames += 1;
      if (frames > 1) return true;
    } else {
      throw new Error("Tệp GIF không hợp lệ.");
    }
  }
  throw new Error("Tệp GIF bị lỗi hoặc thiếu dữ liệu.");
}

function loadSourceImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Không thể đọc ảnh để nén."));
    };
    image.src = url;
  });
}

function exportCanvas(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob && blob.size) resolve(blob);
      else reject(new Error("Không thể xuất ảnh đã nén từ trình duyệt."));
    }, mime, quality);
  });
}

/**
 * Optimize before PocketBase upload, ensuring that even a 5 MiB image is
 * re-encoded. Small images are left byte-for-byte unchanged. Converting large
 * PNGs and static GIFs to WebP keeps transparency, unlike JPEG.
 */
export async function optimizeBlogImage(blob, { filename, mime }) {
  if (blob.size < MAX_IMAGE_BYTES) {
    return { blob, filename, mime, optimized: false };
  }

  if (mime === "image/gif" && await hasMultipleGifFrames(blob)) {
    throw new Error(
      "GIF động " + filename +
      " từ 5 MB trở lên: không thể nén trong trình duyệt mà vẫn giữ chuyển động. " +
      "Vui lòng tối ưu GIF trước khi nhập ZIP.",
    );
  }

  let image;
  try {
    image = await loadSourceImage(blob);
  } catch {
    throw new Error("Không thể mở ảnh " + filename + " để giảm dung lượng.");
  }

  const originalWidth = image.naturalWidth || image.width;
  const originalHeight = image.naturalHeight || image.height;
  if (!originalWidth || !originalHeight) {
    throw new Error("Ảnh " + filename + " có kích thước không hợp lệ.");
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Trình duyệt không hỗ trợ nén ảnh bằng Canvas.");

  const targetMime = mime === "image/jpeg" ? "image/jpeg" : "image/webp";
  const maxSide = Math.max(originalWidth, originalHeight);
  let previousSize = "";
  for (const edge of [2560, 2048, 1600, 1200, 900, 640, 400, 280]) {
    const ratio = Math.min(1, edge / maxSide);
    const width = Math.max(1, Math.round(originalWidth * ratio));
    const height = Math.max(1, Math.round(originalHeight * ratio));
    const sizeKey = width + "x" + height;
    if (sizeKey === previousSize) continue;
    previousSize = sizeKey;
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    for (const quality of [0.86, 0.72, 0.56, 0.42]) {
      let output;
      try {
        output = await exportCanvas(canvas, targetMime, quality);
      } catch {
        continue;
      }
      if (output.size >= MAX_IMAGE_BYTES) continue;

      // Canvas may fall back from WebP to PNG in some browsers.
      const actualMime = output.type || targetMime;
      const extension = actualMime === "image/jpeg" ? ".jpg"
        : actualMime === "image/png" ? ".png"
        : actualMime === "image/webp" ? ".webp" : null;
      if (!extension) continue;
      return {
        blob: output,
        mime: actualMime,
        filename: filename.replace(/\.[^.]+$/, "") + extension,
        optimized: true,
      };
    }
  }
  throw new Error("Không thể giảm ảnh " + filename + " xuống dưới 5 MB.");
}
